import React, {useEffect, useMemo} from "react";
import {Accordion, AccordionDetails, AccordionSummary, Box, Grid, Tooltip, Typography, useTheme} from "@mui/material";
import {ExpandMore} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {useFormContext, useWatch} from "react-hook-form";
import {useRecoilState, useRecoilValue} from "recoil";
import _ from "underscore";
import {ICSAncillary, IRaFillingForm} from "../../../models/models";
import {bookingDataValueState, counterSaleManagementValueState, raFillingAccordionValidationState} from "../../state";
import {FormCheckbox, FormNumberInput, FormTextField} from "../../../common/form-fields-wrappers";
import {formatCurrency, getLocaleByLang} from "../../../utils";
import {deepClone} from "../../../../../util/state/commonFunctions";
import {LANGUAGES, LOCALES} from "../../../config";
import {AncillarySubtype} from "../config";

const AncillariesInfo = () => {
	const theme = useTheme();
	const {t, i18n} = useTranslation(['network']);
	const counterSaleManagement = useRecoilValue(counterSaleManagementValueState);
	const [raFillingAccordionValidation, setRaFillingAccordionValidation] = useRecoilState(raFillingAccordionValidationState);

	const raFillingDataForm = useFormContext<IRaFillingForm>();

	const ancillariesFormValues = raFillingDataForm.getValues('ancillariesInfo');

	const ancillaryName = useMemo((): 'ancillaryNameIt' | 'ancillaryNameEn' => {
		let prop = 'ancillaryDefaultLabel';
		LANGUAGES.every((elem: string) => {
			if (i18n.language.includes(elem)) {
				prop = 'ancillaryName' + elem.charAt(0).toUpperCase() + elem.slice(1);
				return false;
			}
			return true;
		});

		return prop as 'ancillaryNameIt' | 'ancillaryNameEn';
	}, [i18n.language]);

	const handleAccordionOnChange = (e: React.SyntheticEvent, expanded: boolean) => {
		e.stopPropagation();
		
		setRaFillingAccordionValidation((prev) => {
			return {...prev, ["ancillariesInfo"]: expanded};
		})
	}

	return (
		<>
			<Accordion expanded={raFillingAccordionValidation["ancillariesInfo"]} onChange={(e, expanded) => handleAccordionOnChange(e, expanded)}>
				<AccordionSummary expandIcon={<ExpandMore />}>
					<Typography variant={"h6"}>
						{t('raFilling.ancillariesInfo.accordion.title')}
					</Typography>
				</AccordionSummary>

				<AccordionDetails>
					{
						!!counterSaleManagement?.last?.bookedAncillaryList?.length &&
						<Grid container columnSpacing={2} sx={{mb: theme.spacing(4)}}>
							<Grid item xs={12}>
								<Typography variant={"bodyLarge"}>
									{t("raFilling.ancillariesInfo.section.bookedAncillaries")}
								</Typography>
							</Grid>

							{
								// counterSaleManagement?.last?.bookedAncillaryList
								ancillariesFormValues?.booked
									.map((ancillary: any, index: number) =>
										<AncillaryField key={`${ancillary.ancillaryCode}`} elem={ancillary} type={'booked'} index={index} ancillaryName={ancillaryName} />
									)
							}

							<Grid item xs={12} sx={{pt: theme.spacing(2), display: "flex", justifyContent: "space-between"}}>
								<Typography variant={"body"} component={"span"}>
									{t("raFilling.ancillariesInfo.amount.booked")}
								</Typography>

								<Typography variant={"bodyLarge"} component={"span"} sx={{mr: theme.spacing(2)}}>
									<AncillaryTotalCostInfo
										amount={counterSaleManagement?.last?.bookedAncillaryAmount?.amountVatIncluded}
										promoAmount={counterSaleManagement?.last?.bookedAncillaryAmount?.promoAmountVatIncluded}
										currency={counterSaleManagement?.last?.currency}
									/>
								</Typography>
							</Grid>
						</Grid>
					}

					{/* Added Ancillaries */}
					{
						!!counterSaleManagement?.last?.counterSaleAncillaryList?.length &&
						<Grid container columnSpacing={2} sx={{mb: theme.spacing(4)}}>
							<Grid item xs={12}>
								<Typography variant={"bodyLarge"}>
									{t("raFilling.ancillariesInfo.section.addedAncillaries")}
								</Typography>
							</Grid>

							{
								// counterSaleManagement?.last?.counterSaleAncillaryList
								ancillariesFormValues?.counterSale
									.map((ancillary: any, index: number) =>
										<AncillaryField key={`${ancillary.ancillaryCode}`} elem={ancillary} type={'counterSale'} index={index} ancillaryName={ancillaryName} />
									)
							}

							<Grid item xs={12} sx={{pt: theme.spacing(2), display: "flex", justifyContent: "space-between"}}>
								<Typography variant={"body"} component={"span"}>
									{t("raFilling.ancillariesInfo.amount.added")}
								</Typography>
								<Typography variant={"body"} component={"span"} sx={{mr: theme.spacing(2)}}>
									<AncillaryTotalCostInfo
										amount={counterSaleManagement?.last?.counterSaleAncillaryAmount?.amountVatIncluded}
										promoAmount={counterSaleManagement?.last?.counterSaleAncillaryAmount?.promoAmountVatIncluded}
										currency={counterSaleManagement?.last?.currency}
									/>
								</Typography>
							</Grid>
						</Grid>
					}

					{/* Total Amount */}
					{
						!!counterSaleManagement?.last &&
						<Grid container columnSpacing={2} sx={{pt: theme.spacing(2)}}>
							<Grid item xs={12} sx={{display: "flex", justifyContent: "space-between"}}>
								<Typography variant={"bodyLarge"} component={"span"}>
									{t("raFilling.ancillariesInfo.amount.total")}
								</Typography>

								<Typography variant={"bodyLarge"} component={"span"} sx={{mr: theme.spacing(2)}}>
									<AncillaryTotalCostInfo
										amount={counterSaleManagement?.last?.totalAncillaryAmount?.amountVatIncluded}
										promoAmount={counterSaleManagement?.last?.totalAncillaryAmount?.promoAmountVatIncluded}
										currency={counterSaleManagement?.last?.currency}
										fontWeight={"bodyLarge"}
									/>
								</Typography>
							</Grid>
						</Grid>
					}
				</AccordionDetails>
			</Accordion>
		</>
	);
}

export default AncillariesInfo;

/**
 * Ancillary cost info - cost currency /unit
 */
const AncillaryCostInfo = ({elem}: {elem: ICSAncillary}) => {
	const theme = useTheme();
	const {t, i18n} = useTranslation(['network']);

	const locale = useMemo((): 'it-IT' | 'en-IN' | 'fr-FR' => {
		return getLocaleByLang(i18n.language);
	}, [i18n.language]);

	return (
		<>
			{
				(elem.promoPriceVatIncluded === null  || elem.promoPriceVatIncluded === undefined) ?
					<Typography variant={'body'} component={'span'} sx={{mx: theme.spacing(0.5)}}>
						{formatCurrency(locale as LOCALES, elem.priceVatIncluded, elem.currency as string)}{elem.mileageNeeded ? '/km' : elem.maxUnits > 1 ? '/u' : ''}
					</Typography> :
					<>
						<Typography variant={'bodySmall'} component={'span'}>
							(
							<span style={{textDecoration: 'line-through'}}>
								{formatCurrency(locale as LOCALES, elem.priceVatIncluded, elem.currency as string)}
							</span>
							)
						</Typography>
						<Typography variant={'body'} component={'span'} sx={{mx: theme.spacing(1)}}>
							{formatCurrency(locale as LOCALES, elem.promoPriceVatIncluded, elem.currency as string)}{elem.mileageNeeded ? '/km' : elem.maxUnits > 1 ? '/u' : ''}
						</Typography>
					</>
			}
		</>
	);
}

/**
 * Ancillary cost info - cost currency /unit
 */
export const AncillaryTotalCostInfo = ({amount, promoAmount, currency, fontWeight = 'body'}: {amount: number | null; promoAmount: number | null, currency: string, fontWeight?: 'body' | 'bodyLarge'}) => {
	const theme = useTheme();
	const {i18n} = useTranslation(['network']);

	const locale = useMemo((): 'it-IT' | 'en-IN' | 'fr-FR' => {
		return getLocaleByLang(i18n.language);
	}, [i18n.language]);

	return (
		<>
			{
				(promoAmount === null || promoAmount === undefined) ?
					<Typography variant={'body'} component={'span'} sx={{mx: theme.spacing(0.5)}}>
						<strong>{formatCurrency(locale as LOCALES, amount, currency)}</strong>
					</Typography> :
					<>
						<Typography variant={'bodySmall'} component={'span'}>
							<strong>(
								<span style={{textDecoration: 'line-through'}}>
									{formatCurrency(locale as LOCALES, amount, currency)}
								</span>
							)</strong>
						</Typography>
						<Typography variant={fontWeight} component={'span'} sx={{mx: theme.spacing(1)}}>
							<strong>{formatCurrency(locale as LOCALES, promoAmount, currency)}</strong>
						</Typography>
					</>
			}
		</>
	);
}

const AncillaryField = ({elem, type, index, ancillaryName}: {elem: ICSAncillary, type: 'booked' | 'counterSale', index: number, ancillaryName: 'ancillaryNameIt' | 'ancillaryNameEn'}) => {
	const theme = useTheme();
	const raFillingDataForm = useFormContext<IRaFillingForm>();
	const [counterSaleManagement, setCounterSaleManagement] = useRecoilState(counterSaleManagementValueState);
	const bookingDataValue = useRecoilValue(bookingDataValueState);

	const fieldValue = useWatch<IRaFillingForm>({
		name: `ancillariesInfo.${type}.${index}.${elem.mileageNeeded ? 'mileage' : 'units'}`
	}) as number | undefined;

	const manageAncillary = async () => {
		const csmClone = deepClone(counterSaleManagement.last);
		const list = type === 'booked' ? 'bookedAncillaryList': 'counterSaleAncillaryList';
		if (_.isBoolean(fieldValue)) {
			csmClone[list]?.every((ancillary: ICSAncillary) => {
				if (ancillary.ancillaryCode === elem.ancillaryCode) {
					ancillary.units = fieldValue ? 1 : 0;
					return false;
				}
				return true;
			});
		} else {
			csmClone[list]?.every((ancillary: ICSAncillary) => {
				if (ancillary.ancillaryCode === elem.ancillaryCode) {
					if (ancillary.mileageNeeded) {
						ancillary.mileage = fieldValue as number;
					} else {
						ancillary.units = fieldValue as number;
					}
					return false;
				}
				return true;
			});
		}
		await setCounterSaleManagement({previous: counterSaleManagement.last, last: csmClone});
	}

	useEffect(() => {
		manageAncillary();
	}, [fieldValue])

	const storeTextValue = (event: React.SyntheticEvent, ancillary: ICSAncillary, type: 'booked' | 'counterSale') => {
		const cs = deepClone(counterSaleManagement);
		let list = [];
		if (type === 'booked') {
			list = cs.last.bookedAncillaryList;
		} else {
			list = cs.last.counterSaleAncillaryList;
		}

		for (let i = 0; i < list.length; i++) {
			if (list[i].ancillaryCode === ancillary.ancillaryCode) {
				// @ts-ignore
				list[i].address = event?.target?.value;
				break;
			}
		}

		setCounterSaleManagement({previous: counterSaleManagement.last, last: cs.last});
	}

	if (elem.maxUnits > 1 || elem.mileageNeeded) {

		return (
			<Grid container item xs={12} key={`${type}_${elem.ancillaryCode}`} sx={{py: theme.spacing(1), mb: 'auto'}}>
				<Grid item xs={8} sx={{display: "flex", alignItems: "flex-end", justifyContent: "flex-start"}}>
					<Box sx={{width: '100px'}}>
						<FormNumberInput<IRaFillingForm>
							name={`ancillariesInfo.${type}.${index}.${elem.mileageNeeded ? 'mileage' : 'units'}`}
							label={''}
							control={raFillingDataForm.control}
							minValue={0}
							maxValue={elem.mileageNeeded ? 999 : elem.maxUnits}
							disabled={!elem.deselectable || bookingDataValue.bookingDetail.flagSubstitution || elem.subtype === AncillarySubtype.AdditionalDrivers || elem.ancillarySubType === AncillarySubtype.AdditionalDrivers}
						/>
					</Box>
					<Tooltip title={elem[ancillaryName] as string} disableInteractive sx={{pt: theme.spacing(2)}}>
						<Typography variant={'body'} sx={{ml: theme.spacing(2), py: theme.spacing(1)}}>
							{elem[ancillaryName]}
						</Typography>
					</Tooltip>
				</Grid>
				<Grid item xs={4} sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', py: theme.spacing(1), pr: theme.spacing(2)}}>
					<AncillaryCostInfo elem={elem}/>
				</Grid>

				{
					(['DELIVERY', 'COLLECTION'].includes(elem.ancillarySubType as string) || ['DELIVERY', 'COLLECTION'].includes(elem.subtype as string)) && !!fieldValue && fieldValue > 0 &&
					<Grid item xs={12} sx={{display: "flex", alignItems: "flex-end", justifyContent: "flex-start", pr: theme.spacing(2)}}>
						<FormTextField<IRaFillingForm>
							name={`ancillariesInfo.${type}.${index}.address`}
							label={elem[ancillaryName]}
							control={raFillingDataForm.control}
							required={!!fieldValue && fieldValue > 0}
							disabled={bookingDataValue.bookingDetail.flagSubstitution}
							onBlur={(event: React.SyntheticEvent) => storeTextValue(event, elem, type)}
						/>
					</Grid>
				}
			</Grid>
		)
	} else {
		return (
			<Grid container item xs={12} key={`${type}_${elem.ancillaryCode}`} sx={{py: theme.spacing(1)}}>
				<Grid item xs={8} sx={{display: "flex", alignItems: "flex-end", justifyContent: "flex-start", pt: theme.spacing(2)}}>
					<Tooltip title={elem.ancillaryDescription ?? ''} disableInteractive sx={{display: "flex", alignItems: "flex-end"}}>
						<span>
							<FormCheckbox<IRaFillingForm>
								name={`ancillariesInfo.${type}.${index}.units`}
								label={elem[ancillaryName]}
								disabled={!elem.deselectable || bookingDataValue.bookingDetail.flagSubstitution}
                                readOnly={elem.subtype === AncillarySubtype.AdditionalDrivers || elem.ancillarySubType === AncillarySubtype.AdditionalDrivers}
								// defaultValue={['A', 'Z'].includes(params.sellType) ? true : false}
								// disabled={['A', 'Z'].includes(params.sellType) || step === InsertBookingStep.CUSTOMER_DETAILS}
							/>
						</span>
					</Tooltip>
				</Grid>
				<Grid item xs={4} sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', py: theme.spacing(1), pr: theme.spacing(2)}}>
					<AncillaryCostInfo elem={elem}/>
				</Grid>

				{
					(['URBAN_DELIVERY', 'URBAN_COLLECTION'].includes(elem.ancillarySubType as string) || ['URBAN_DELIVERY', 'URBAN_COLLECTION'].includes(elem.subtype as string)) && !!fieldValue &&
					<Grid item xs={12} sx={{display: "flex", alignItems: "flex-end", justifyContent: "flex-start", pr: theme.spacing(2)}}>
						<FormTextField<IRaFillingForm>
							name={`ancillariesInfo.${type}.${index}.address`}
							label={elem[ancillaryName]}
							control={raFillingDataForm.control}
							required={!!fieldValue}
							disabled={bookingDataValue.bookingDetail.flagSubstitution}
							onBlur={(event: React.SyntheticEvent) => storeTextValue(event, elem, type)}
						/>
					</Grid>
				}
			</Grid>
		)
	}
}